import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DashboardService } from '@features/dashboard/services/dashboard.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { DashboardMetrics } from '@features/dashboard/models/dashboard.model';

/** One headline number with its caption and footnote. */
interface Metric {
  label: string;
  value: string;
  unit?: string;
  note: string;
  /** Only "Fehlgeschlagen" earns a colour — see the badge palette rationale. */
  alert?: boolean;
}

@Component({
  selector: 'app-dashboard-metrics',
  standalone: true,
  templateUrl: './dashboard-metrics.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardMetricsComponent implements OnInit {
  private dashboardService = inject(DashboardService);
  private currentOrgStore = inject(CurrentOrganizationStore);

  private data = signal<DashboardMetrics | null>(null);
  isLoading = signal(true);

  metrics = computed<Metric[]>(() => {
    const m = this.data();
    if (!m) return [];

    const decided = m.won + m.lost;
    const rate = decided > 0 ? Math.round((m.won / decided) * 100) : 0;

    return [
      {
        label: 'Analysen',
        value: this.format(m.totalAnalyses),
        note: `+${this.format(m.createdThisWeek)} diese Woche`,
      },
      {
        label: 'In Bearbeitung',
        value: this.format(m.inProgress),
        unit: 'aktiv',
        note: `${this.format(m.queued)} in Warteschlange`,
      },
      {
        label: 'Fehlgeschlagen',
        value: this.format(m.failed),
        note: `${this.format(m.failedLast24h)} in den letzten 24 Std.`,
        alert: m.failed > 0,
      },
      {
        // Share of decided analyses, not of all — an untouched analysis is not
        // a loss, and counting it as one would drag the rate toward zero.
        label: 'Abschlussquote',
        value: String(rate),
        unit: '%',
        note: `${this.format(m.won)} gewonnen · ${this.format(m.lost)} verloren`,
      },
    ];
  });

  async ngOnInit(): Promise<void> {
    const orgId = this.currentOrgStore.organization()?.id;
    if (!orgId) {
      this.isLoading.set(false);
      return;
    }
    try {
      this.data.set(await this.dashboardService.getMetrics(orgId));
    } catch (error) {
      console.error('[DashboardMetrics] Failed to load metrics:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  private format(value: number): string {
    return value.toLocaleString('de-DE');
  }
}
